/**
 * Écran de détail de la file de synchronisation
 * (`ARCHITECTURE_V2.md` §3.5, étape 3).
 *
 * 🔴 **C'est ici que se tient la promesse du §3.3 : « une opération `failed`
 * n'est jamais supprimée en silence. Elle reste visible avec son motif — sans
 * quoi une séance disparaîtrait sans que rien ne le dise, ce qui est exactement
 * le défaut que le hors ligne existe pour empêcher. »**
 *
 * 🔴 **Et c'est ici qu'on dit la vérité sur *quand* ça part.** La file tourne
 * dans la page, pas dans le service worker : Background Sync n'existe pas sur
 * Safari iOS (§3.3). Une saisie faite hors ligne **ne part pas toute seule dans
 * la poche** — elle part à la réouverture de l'application avec du réseau. Ne
 * pas l'écrire laisserait croire à un envoi en arrière-plan que la plateforme ne
 * fournit pas, et l'utilisateur découvrirait le contraire le jour où ça compte.
 */

import { Button, Card, Hint, PendingNote } from '../../components/ui/primitives';
import { TOKENS } from '../../components/ui/tokens';
import { useSyncState } from '../../hooks/useSyncState';
import { discardFailed, drainSyncQueue, retryFailed, type SyncOp } from '../../lib/syncQueue';

/** « POST /api/meals/12/entries » — de quoi reconnaître l'opération sans la deviner. */
function describe(op: SyncOp): string {
  return `${op.method} ${op.path}`;
}

function FailedRow({ op }: { op: SyncOp }) {
  return (
    <Card>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <span style={{ fontSize: '0.95rem', fontWeight: 500 }}>{describe(op)}</span>

        {/*
          Le motif, en toutes lettres. C'est *le* contenu de cet écran : sans lui
          on saurait qu'une opération a échoué sans savoir laquelle ni pourquoi,
          ce qui ne permet aucune décision.
        */}
        <span style={{ fontSize: '0.85rem', color: TOKENS.textQuiet }}>
          {op.lastError ?? 'Refusée par le serveur.'}
        </span>

        <span style={{ fontSize: '0.75rem', color: TOKENS.textFaint }}>
          {new Date(op.createdAt).toLocaleString('fr-FR')} · {op.attempts} tentative(s)
        </span>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {/*
            ⚠️ **Réessayer a un sens malgré le « définitif » du §3.3.** Le §3.3
            dit qu'un 4xx ne se retente pas *tout seul*, et il a raison : rejouer
            en boucle une requête refusée pour sa forme ne la rendra pas valide.
            Mais la cause peut avoir disparu entre-temps — un `409 food_in_use`
            se lève dès qu'on a retiré l'ingrédient de la recette. Ce qui était
            proscrit, c'est la boucle automatique, pas le geste délibéré.
          */}
          <Button
            variant="quiet"
            onClick={() => {
              retryFailed(op.seq);
            }}
          >
            Réessayer
          </Button>
          {/*
            ⚠️ Le seul chemin par lequel une opération quitte la file sans avoir
            été acquittée — et il passe par quelqu'un qui a lu le motif.
            Ses dépendantes partent avec elle : une entrée dans un repas
            abandonné n'a nulle part où aller.
          */}
          <Button
            variant="danger"
            onClick={() => {
              discardFailed(op.seq);
            }}
          >
            Abandonner
          </Button>
        </div>
      </div>
    </Card>
  );
}

export function SyncSection() {
  const { pending, failed, paused } = useSyncState();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <Hint>
        Les saisies faites sans réseau sont conservées sur cet appareil, puis
        envoyées dans l’ordre où elles ont été faites. Elles partent{' '}
        <strong>à l’ouverture de l’application avec du réseau</strong> — iOS ne
        permet pas de les envoyer en arrière-plan, application fermée.
      </Hint>

      {pending === 0 && failed.length === 0 && (
        <Card>
          <span style={{ fontSize: '0.95rem' }}>Tout est envoyé.</span>
        </Card>
      )}

      {pending > 0 && (
        <PendingNote>
          {pending} saisie(s) en attente d’envoi.
          {paused && ' Envoi suspendu : reconnectez-vous pour reprendre.'}
        </PendingNote>
      )}

      {pending > 0 && (
        <Button
          onClick={() => {
            void drainSyncQueue();
          }}
        >
          Envoyer maintenant
        </Button>
      )}

      {failed.length > 0 && (
        <>
          <h2 style={{ margin: 0, fontSize: '1rem' }}>
            {failed.length} opération(s) refusée(s)
          </h2>
          <Hint>
            Celles-ci ne repartiront pas d’elles-mêmes : le serveur les a
            refusées, et les rejouer telles quelles donnerait le même refus.
          </Hint>
          {failed.map((op) => (
            <FailedRow key={op.seq} op={op} />
          ))}
        </>
      )}
    </div>
  );
}
